// @flow
import * as React from 'react';
import { reisy, use } from 'reisy';
import { observer } from 'mobx-react';
import { IntlProvider } from 'react-intl';
import type Player from './player.js';
import track, { initAnalytics } from '../analytics/analytics.js';
import CookieConsent from '../components/cookie-consent';
import PropTypes from 'prop-types';

//let DevTool = null;
if (process.env.NODE_ENV === 'development') {
	const DevTools = require('mobx-react-devtools');
	DevTools.configureDevtool({
		logEnabled: true,
	});
	//DevTool = <DevTools.default position={{top: 0, left: 100}} />;
}

export const styles = reisy`
@namespace App;

JackInTheBoxScrollCorrection: $Settings.JackInTheBoxScrollCorrection;
Branding: $Settings.Branding;
ForceIndexPublishableTitle: $Settings.ForceIndexPublishableTitle;
EnableRTL: false;

Container {
	font-size: 16px;
}
`;
reisy.prefix('.pagestrip-root');

function robotsShouldIndex(player, content) {
   if (!content || !player) return false;

   return player.options.jackInTheBox ?
      // if embedded, it's search index enabled irregardless of the publisher
      // setting, and only not searchable if it's disabled specifically in 
      // the story. that is, because we want to disable search indexing on our
      // own platform if the content is only embedded somewhere. Since the
      // publisher can also be the content itself, we have to check for this
      // specifically.
      (!!content.isSearchIndexingEnabled || "publisher" == content.type) :
      // on our own platform, you can disable search indexing for the whole
      // publisher in the publisher settings or disable it specifically per
      // story, but if search indexing is disabled in the publisher, all stories
      // should have it disabled.
      (!!content.isSearchIndexingEnabled && content.publisher.isSearchIndexingEnabled)
}

function maybeSetRobotsMeta(player, shouldIndex) {
	let status = shouldIndex ? 'all' : 'noindex';

	if (IS_BROWSER) {
		let robotMeta = document.querySelector('meta[name=robots]');
		if (!robotMeta) {
			robotMeta = document.createElement('meta');
			robotMeta.name = 'robots';
			robotMeta.setAttribute('data-ps-meta', true);
			document.head.appendChild(robotMeta);
		}
		if (
			robotMeta.hasAttribute('data-ps-meta') ||
			!player.options.jackInTheBox
		) {
			robotMeta.content = status;
		}
	}
}

/**
 * Helper function to update document title and analytics
 * @param {Player} player - The current player
 * @param {boolean=} branding - If `true` branding will be added to the title
 * @return {string} - a string of test information for automated testing
 */
function doTitleAndAnalytics(player: Player, branding: boolean) {
	const { api, options } = player;
	const { url, query, location, action, hierarchy } = player.url;
	const id = hierarchy.length
		? hierarchy[hierarchy.length - 1]
		: options.context;
	if (!id) {
		return '';
	}

	const content = api.resolveSync(id);
	const publisher = api.resolveSync(content && content.publisher && content.publisher.id || '');

	const parts = [];
	if (content) {
		if (content.type === 'collection' || content.type === 'story') {
			parts.push(content.title);
			parts.push(content.publisher.name);
		} else {
			if (styles.ForceIndexPublishableTitle && content.index) {
				parts.push(content.index.title);
			}
			parts.push(content.name);
		}
	}
	if (branding) {
		parts.push('pagestrip');
	}

	const title = parts.join(' \u2013 ');
	const origin = `${location.protocol}//${location.host}`;

	if (IS_BROWSER) {
		if (!options.keepTitle) {
			document.title = title;
		}

		if (content && publisher) {
			// this is the full url including the context
			const fullUrl = player.url.create({}, { withContext: true });
			const absolute =
				(content.publisher && content.publisher.canonicalBaseUrl) ||
				options.canonicalBaseUrl ||
				'';
			const hasHashbang = !!~absolute.indexOf('#!');
			const withContext =
				(options.history === 'browser' &&
					!hasHashbang &&
					!~absolute.indexOf(`?${options.historyQueryParam}`)) ||
				absolute.length <= 1
					? undefined
					: false;
			const canonicalUrl = player.url
				.create(
					//hasHashbang ? { query: { q: undefined, in: undefined } } : {},
					{ query: { q: undefined, in: undefined } },
					{ absolute, withContext },
				)
				.replace(/(&|\?)page=(1|flow)/, '')
			
			track(
				(options.trackCanonical || publisher.trackCanonical) ? 
					canonicalUrl : 
					window.location.pathname +
						window.location.search +
						window.location.hash,
				content,
				title,
				options,
			);

			if (options.rewriteCanonical) {
				let canonical = document.querySelector('link[rel=canonical]');
				if (!canonical) {
					canonical = document.createElement('link');
					canonical.setAttribute('rel', 'canonical');
					document.head.appendChild(canonical);
				}
				canonical.setAttribute(
					'href',
					canonicalUrl,
				);
			}

			if (options.rewriteAlternateMobile) {
				let alt = document.querySelector(
					'link[rel=alternate][media="only screen and (max-width: 800px)"]',
				);
				const setAlt = !!player.url.hierarchy.length;
				if (!alt && setAlt) {
					alt = document.createElement('link');
					alt.setAttribute('rel', 'alternate');
					alt.setAttribute('media', 'only screen and (max-width: 800px)');
					document.head.appendChild(alt);
				} else if (alt && !setAlt) {
					alt.parentElement.removeChild(alt);
				}
				if (alt) {
					alt.setAttribute(
						'href',
						player.url.create(
							{ query: { page: 'flow' } },
							{ absolute, withContext },
						),
					);
				}
			}

			const metaDesc = content.shareDescription || content.description;
			if (metaDesc && options.rewriteMetaDescription) {
				let meta = document.querySelector('meta[name=description]');
				let prefixedMeta = document.querySelectorAll('meta[name$=":description"],meta[property$=":description"]');
				if (!meta) {
					meta = document.createElement('meta');
					meta.name = 'description';
					document.head.appendChild(meta);
				}
				meta.content = metaDesc;
				Array.prototype.forEach.call(prefixedMeta, meta => {
					meta.content = metaDesc;
				})
			}

			if (options.isEmbedded && !('embedwidget' in query)) {
				window.parent.postMessage(
					{
						type: 'kent-navigating',
						action,
						origin,
						url,
						fullUrl,
						title,
					},
					'*',
				);
			}

			if (options.jackInTheBox) {
				window.postMessage(
					{
						type: 'kent-navigating',
						action,
						origin,
						url,
						fullUrl,
						title,
					},
					'*',
				);
			}
		}

		maybeSetRobotsMeta(player, robotsShouldIndex(player, content));
	}
   
	// I think this is actually a neat way to output things we want to test for!
	const tests = { title, action, origin, url };
	return `/*${JSON.stringify(tests, undefined, 2)}*/`;
}

/**
 * Store of scroll position per url for resetting scroll positions after some navigation
 * that is handled by us
 */
const SCROLL_POSITIONS: { [key: string]: ?number } = Object.create(null);

type Props = {
	player: Player,
};

/**
 * Helper component to observe changes in the mobx state and update title, analytics and
 * scroll position accordingly
 */
class _App extends React.Component {
	props: Props;
	url: string;

	constructor(props: Props) {
		super(props);
		this.url = this.props.player.url.url;
		this.root = React.createRef();

		let trackers = [];
		const { tracking, jackInTheBox, noPSTracking } = this.props.player.options;
		if (tracking) {
			if (tracking.onTrack) {
				trackers.push({
					type: 'cb',
					trackPage: tracking.onTrack
				})
			}
			if (tracking.oewa) {
				trackers.push({
					type: 'oewa',
					url: tracking.oewa
				})
			}
		}

		if (IS_BROWSER) {
			const { publisher = { analytics: {}, appearance: {} } } = this.props.player;
			const { 
				analytics: { 
					trackers: publisherTrackers = [],
					useAnalytics = true, 
					hasCookieBanner = false,
					...options
				}, 
				appearance: { 
					styles: publisherStyles = [] 
				},
			} = publisher;
			
			if (useAnalytics) {
				trackers.push(...(publisherTrackers.filter(
					t => (!noPSTracking || t.scope !== 'pagestrip') && (
						!jackInTheBox || 
						t.scope === 'pagestrip' && t.injectInEmbed !== false || 
						t.injectInEmbed === true)
				)));
				initAnalytics({
					trackers,
					skipFirstTrackOnDiscovered: jackInTheBox,
					hasCookieBanner: hasCookieBanner || publisherStyles.some(({ns, name, def}) => ns === 'CookieConsent' && name === 'show' && def),
					overrideGTMPageViewEventName: (publisherStyles.find(({ns, name, def}) => ns === 'Settings' && name === 'overrideGTMPageViewEventName') || {}).def,
					...options
				});
			}
		}
	}

	componentWillUpdate() {
		SCROLL_POSITIONS[this.url] = window.pageYOffset;
	}

	componentDidUpdate() {
		const { content, url } = this.props.player;
		this.url = url.url;
		// When we have a story, the reader itself is responsible to reset the
		// scroll position.
		// also if content has appendPaging and page > 1 we can ignore scroll reset
		if (
			content &&
			((content.appendPaging && content.currentPage > 1) ||
				content.type === 'story' ||
				(content.index && content.index.type === 'story'))
		) {
			return;
		}
		window.scrollTo(
			0,
			SCROLL_POSITIONS[this.url] ||
				(this.props.player.options.jackInTheBox
					? Math.max(
							0,
							window.pageYOffset + this.root.current.getBoundingClientRect().top + (styles.JackInTheBoxScrollCorrection || 0),
					  )
					: 0),
		);
	}

	render() {
		const { player } = this.props;
		const { publisher: { locales } = {} , url: { locale } = {} } = player;

		let needRTL = false;
		if (styles.EnableRTL && locales && locale) {
			needRTL = locales.some(loc => {
				if (loc.code && loc.code === locale) {
					return loc.bidi;
				}
			});
		}

		return <div {...use(styles.Container, 'pagestrip-root', needRTL ? 'pagestrip-root--rtl' : null)} ref={this.root}>
				{player.element}
				<CookieConsent />
				<script>{doTitleAndAnalytics(player, styles.Branding)}</script>
			</div>
	}
}
const App = observer(_App);

/**
 * Additional wrapper to add intl provider to app. This component is used by the client &
 * server side rendering to actually render the player into the respective DOM.
 */
class Provider extends React.Component {
	props: Props;

	static childContextTypes = {
		player: PropTypes.any,
	};

	getChildContext() {
		const { player } = this.props;
		return { player };
	}

	render() {
		const { player } = this.props;
		const { options } = player;
		const { locale } = options;
		return (
			<IntlProvider key={locale} locale={locale} messages={options.messages}>
				<App player={player} />
			</IntlProvider>
		);
	}
}

export default observer(Provider);



// WEBPACK FOOTER //
// ./lib/player/app.js